import axios, {
  type AxiosInstance,
} from 'axios';
import type { PopupRequest } from '@azure/msal-browser';

import {
  API_URL,
  HUB_API_URL,
  INVENTORY_API_URL,
  MSAL_CLIENT_ID,
  PROXY_API_BASE_URL,
  AI_ANALYZER_API_URL,
} from '@/config/env';
import { conformanceRequest, hubRequest, inventoryRequest } from '@/config/msalConfig';
import { getToken } from './utils';

const attachAuthInterceptors = (api: AxiosInstance, request: PopupRequest) => {
  api.interceptors.request.use(async (config) => {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${await getToken(request)}`;
    return config;
  });

  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error?.config;

      // A 401 means the API rejected the caller, which is not the same as the token
      // having expired: a missing app role answers 401 with a perfectly valid token.
      // Mint a fresh token once and replay the request; if it is rejected again the
      // error surfaces, because a full-page login cannot grant a role and would only
      // bounce the user through Entra and back into the same 401.
      if (error?.response?.status === 401 && config && !config.__freshTokenRetry) {
        config.__freshTokenRetry = true;

        try {
          await getToken(request, { forceRefresh: true });
          return await api.request(config);
        } catch {
          // getToken escalates to interactive auth on InteractionRequiredAuthError,
          // which is the only case where re-login is the actual remedy.
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    },
  );
};

export const inventoryApi = axios.create({
  baseURL: INVENTORY_API_URL,
});

attachAuthInterceptors(inventoryApi, inventoryRequest);

export const conformanceApi = axios.create({
  baseURL: API_URL,
});

attachAuthInterceptors(conformanceApi, conformanceRequest);

export const hubApi = axios.create({
  baseURL: HUB_API_URL,
});

attachAuthInterceptors(hubApi, hubRequest);

export const aiAnalyzerApi = axios.create({
  baseURL: AI_ANALYZER_API_URL,
});

export const proxyApi = axios.create({
  baseURL: PROXY_API_BASE_URL,
});

const proxyRequest = {
  scopes: [`api://${MSAL_CLIENT_ID}/user_impersonation`],
};

attachAuthInterceptors(proxyApi, proxyRequest);
